import dayjs from "dayjs";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Eye, Loader2, Lock } from "lucide-react";

import FormAnswer from "@/components/form/form-answer/form-answer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFormStore } from "@/store";
import { useFormResponseStore } from "@/store/form/use-form-response-store";
import { useFormDraftStorage } from "@/hooks/form/use-form-draft-storage";
import { delegationSession } from "@/lib/delegation-session";
import { delegationService } from "@/services/delegation/delegation.service";
import type { DelegationSectionResponse, DelegationView } from "@/types";

const AUTOSAVE_DEBOUNCE_MS = 2000;

/** The wire shape is a list of sections; the store keys them by section id. */
const toStoreShape = (sections: DelegationSectionResponse[] | null) => {
  const shaped: Record<string, any> = {};
  (sections ?? []).forEach((section) => {
    if (!section?.sectionId) return;
    shaped[section.sectionId] = {
      sectionId: section.sectionId,
      sectionName: section.sectionName,
      questions: Object.fromEntries(
        (section.responses ?? []).map((answer) => [answer.questionId, answer])
      ),
    };
  });
  return shaped;
};

/**
 * The page a delegation link opens. It runs with no session at all: the token
 * in the URL is the whole credential, so the shared form components reach the
 * token-scoped endpoints through `delegationSession` rather than the normal
 * authenticated services.
 */
const DelegationFormPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const { setFormId, setSections } = useFormStore();
  const { formResponses, hydrateResponses, resetResponses } =
    useFormResponseStore();

  const [view, setView] = useState<DelegationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [, setCurrentSectionId] = useState<string | null>(null);

  const canEdit = !!view?.canEdit;
  const sender = view?.sentBy.name || view?.sentBy.email || "";

  const { clearDraft } = useFormDraftStorage({
    userId: view?.you.email,
    processId: "delegation",
    formId: view?.form?.id,
    applicantProcessId: view?.delegationId,
    enabled: !!view?.form && canEdit,
  });

  // Make the token available to the shared services before anything fetches.
  useEffect(() => {
    delegationSession.set(token ?? null);
    return () => {
      delegationSession.set(null);
      resetResponses();
      setSections([]);
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    delegationService
      .view(token)
      .then((data) => {
        if (cancelled) return;
        setView(data);
        if (data.form) {
          setFormId(data.form.id);
          setSections(data.form.design ?? []);
          hydrateResponses(toStoreShape(data.responses));
        }
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Autosave to the server, on top of the local mirror the storage hook keeps.
  const dirty = useRef(false);
  const transformed = useMemo(
    () =>
      Object.values(formResponses).map((section: any) => ({
        sectionId: section.sectionId,
        sectionName: section.sectionName,
        responses: Object.values(section.questions),
      })),
    [formResponses]
  );

  useEffect(() => {
    if (!canEdit || Object.keys(formResponses).length === 0) return;

    // The first pass after hydration is the server's own copy coming back.
    if (!dirty.current) {
      dirty.current = true;
      return;
    }

    const timer = setTimeout(() => {
      delegationService
        .save(transformed)
        .then((res) => setSavedAt(res.savedAt ?? new Date().toISOString()))
        .catch(() => undefined);
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [canEdit, transformed]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await delegationService.save(transformed);
      setSavedAt(res.savedAt ?? new Date().toISOString());
      toast.success(t("delegation.saved", { time: dayjs().format("h:mm A") }));
    } catch {
      toast.error(t("delegation.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await delegationService.submit(transformed);
      if (!res.success) throw new Error();
      clearDraft();
      setConfirmOpen(false);
      setView((prev) =>
        prev
          ? { ...prev, status: "SUBMITTED", canEdit: false, submittedAt: new Date().toISOString() }
          : prev
      );
      toast.success(t("delegation.submitted"));
    } catch {
      toast.error(t("delegation.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-16 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("delegation.loading")}
        </div>
      </Shell>
    );
  }

  if (notFound || !view) {
    return (
      <Shell>
        <Notice
          icon={<AlertCircle className="h-6 w-6 text-red-600" />}
          title={t("delegation.invalidLink")}
          body={t("delegation.invalidLinkHelp")}
        />
      </Shell>
    );
  }

  if (view.status !== "PENDING" || !view.form) {
    return (
      <Shell>
        <Notice
          icon={<CheckCircle2 className="h-6 w-6 text-darkBlue" />}
          title={terminalTitle(view, t)}
          body={terminalBody(view, sender, t)}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl">{view.form.name}</CardTitle>
          <p className="text-sm text-gray-600">
            {t("delegation.sentBy", { name: sender })}
          </p>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="flex items-center gap-2 font-medium text-darkBlue">
            {canEdit ? (
              t("delegation.youAreEditor")
            ) : (
              <>
                <Eye className="h-4 w-4" />
                {t("delegation.youAreViewer")}
              </>
            )}
          </p>
          {view.message && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">
                {t("delegation.theirNote")}
              </div>
              <p className="whitespace-pre-wrap text-gray-800">{view.message}</p>
            </div>
          )}
          <p className="text-xs text-gray-500">
            {t("delegation.expiresOn", {
              date: dayjs(view.expiresAt).format("MMM D, YYYY"),
            })}
          </p>
        </CardContent>
      </Card>

      {/*
        Viewers get the same rendering with input suppressed. This is presentation
        only — every write is refused server-side by the editor guard.
      */}
      <div
        className={
          canEdit ? undefined : "pointer-events-none select-none opacity-80"
        }
        aria-disabled={!canEdit}
      >
        <FormAnswer setCurrentFormSectionId={setCurrentSectionId} />
      </div>

      {canEdit ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="rounded border border-darkBlue px-4 py-2 text-sm text-darkBlue disabled:opacity-60"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t("delegation.saving") : t("delegation.save")}
          </button>
          <button
            type="button"
            className="main-dark-button text-sm"
            onClick={() => setConfirmOpen(true)}
            disabled={submitting}
          >
            {t("delegation.submit")}
          </button>
          {savedAt && (
            <span className="text-xs text-gray-500">
              {t("delegation.saved", { time: dayjs(savedAt).format("h:mm A") })}
            </span>
          )}
        </div>
      ) : (
        <p className="mt-6 flex items-center gap-2 text-sm text-gray-500">
          <Lock className="h-4 w-4" />
          {t("delegation.viewerReadOnly")}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-[520px] sm:rounded-md">
          <DialogHeader>
            <DialogTitle>{t("delegation.submitConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("delegation.submitConfirmBody", { name: sender })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              onClick={() => setConfirmOpen(false)}
            >
              {t("delegation.cancel")}
            </button>
            <button
              type="button"
              className="main-dark-button text-sm"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? t("delegation.submitting") : t("delegation.confirm")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-darkBlue py-5 text-center text-xl font-semibold text-white">
      UzaForms
    </div>
    <div className="mx-auto w-full max-w-3xl px-4 py-6">{children}</div>
  </div>
);

const Notice: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <Card>
    <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
      {icon}
      <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      <p className="max-w-md text-sm text-gray-600">{body}</p>
    </CardContent>
  </Card>
);

const terminalTitle = (view: DelegationView, t: (k: string) => string) => {
  if (view.status === "SUBMITTED") return t("delegation.statusSubmittedTitle");
  if (view.status === "APPROVED") return t("delegation.statusApprovedTitle");
  return t("delegation.statusClosedTitle");
};

const terminalBody = (
  view: DelegationView,
  sender: string,
  t: (k: string, o?: Record<string, string>) => string
) => {
  if (view.status === "SUBMITTED") {
    return t("delegation.statusSubmittedBody", {
      date: dayjs(view.submittedAt ?? undefined).format("MMM D, YYYY"),
      name: sender,
    });
  }
  if (view.status === "APPROVED") {
    return t("delegation.statusApprovedBody", { name: sender });
  }
  return view.closedReason
    ? t(`delegation.closed${view.closedReason}`, { name: sender })
    : t("delegation.closedFallback");
};

export default DelegationFormPage;
